import { useState, type ComponentType, type ReactNode } from 'react';
import { ArrowUpRight, Shield, Compass, Crown, Camera, Star, BookOpen, Feather, FileText } from 'lucide-react';
import PrefetchLink from '@/components/shared/PrefetchLink';
import { Skeleton } from '@/components/ui/skeleton';

import rangerInterior from '@/assets/heritage/lodge-zebra-saddle-trio-1990s.jpg';
import longhornImg from '@/assets/heritage/award-ceremony-longhorn-mount-jorns.jpg';
import visitorImg from '@/assets/visitors/grace-kelly-rainier-1960.webp';
import rangersImg from '@/assets/rangers/gonzaullas-hof.jpg';
import exoticWildlifeImg from '@/assets/wildlife/axis-deer-herd-large.jpg';
import vaultLibrary from '@/assets/vault-library.jpg';
import captainSchreinerPortrait from '@/assets/book/captain-schreiner-portrait.jpg';
import connallySchreinerIII from '@/assets/connally-schreiner-iii.jpeg';
import firstPeoplesImg from '@/assets/heritage/guadalupe-river-bluffs-vintage.jpg';
import firstLeasePage1 from '@/assets/first-hunting-lease/page-1.jpg.asset.json';

/**
 * Editorial Noir Bento — landing-page collections overview.
 *
 * A self-contained dark, brass-accented bento section that surfaces the
 * museum's primary collections. Replaces the legacy ExploreSection +
 * CultureFameSection on the home page. All tiles route to existing
 * canonical pages, all imagery is archival (no AI / no stock), and the
 * "appointment only" constraint is honored in the hero copy.
 *
 * Palette is pinned via the existing brass design tokens so the dark
 * surface stays consistent with the rest of the site's brand language.
 */

type TileImg = {
  src: string;
  alt: string;
};

const BentoTileImage = ({ src, alt, eager = false }: TileImg & { eager?: boolean }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <>
      {!loaded && !failed && (
        <Skeleton className="absolute inset-0 rounded-none bg-brass/10" />
      )}
      {!failed && (
        <img
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`absolute inset-0 w-full h-full object-contain transition-all duration-1000 ${
            loaded ? 'opacity-70' : 'opacity-0'
          } scale-75 grayscale-0 lg:scale-[0.6] lg:opacity-50 lg:grayscale lg:group-hover:opacity-70 lg:group-hover:scale-75 lg:group-hover:grayscale-0`}
        />
      )}
    </>
  );
};

/**
 * Shared standard bento tile. All non-hero tiles use this component so
 * height, padding, image treatment, eyebrow, title, description, and
 * arrow indicator stay pixel-identical.
 *
 * The two prior size variants (col-span-1 wildlife/rangers and the
 * col-span-4 Charles Schreiner III banner) have been consolidated
 * into a single half-width tile (`md:col-span-2`).
 */
type StandardTileProps = {
  to: string;
  image: string;
  imageAlt: string;
  eyebrow: string;
  eyebrowIcon?: ComponentType<{ className?: string }>;
  title: string;
  description: ReactNode;
};

const StandardTile = ({
  to,
  image,
  imageAlt,
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  description,
}: StandardTileProps) => (
  <PrefetchLink
    to={to}
    className="md:col-span-2 relative overflow-hidden bg-[hsl(25_22%_14%)] p-7 lg:p-8 flex flex-col justify-between border border-brass/10 hover:border-brass/40 transition-all group min-h-[240px]"
  >
    <BentoTileImage src={image} alt={imageAlt} />
    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(25_22%_14%)] via-[hsl(25_22%_14%)]/85 to-[hsl(25_22%_14%)]/40" />
    <div className="space-y-2 relative z-10">
      <span className="text-brass-light text-[10px] uppercase tracking-[0.3em] flex items-center gap-1.5">
        {EyebrowIcon ? <EyebrowIcon className="w-3 h-3" /> : null}
        {eyebrow}
      </span>
      <h3 className="font-display text-2xl lg:text-3xl text-[hsl(40_35%_94%)] group-hover:text-brass-light transition-colors">
        {title}
      </h3>
    </div>
    <p className="text-[hsl(40_30%_96%)]/55 text-sm italic font-serif leading-relaxed relative z-10 max-w-sm">
      {description}
    </p>
    <ArrowUpRight className="absolute top-5 right-5 w-4 h-4 text-brass opacity-0 group-hover:opacity-100 transition-opacity" />
  </PrefetchLink>
);

const BentoExplore = () => {
  return (
    <section className="bg-[hsl(25_25%_8%)] text-[hsl(40_30%_96%)]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {/* Section eyebrow */}
        <div className="flex items-end justify-between mb-10 border-b border-brass/20 pb-6">
          <div>
            <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-brass-light mb-2">
              The Collections
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-[hsl(40_30%_96%)] tracking-wide">
              Pathways Into History
            </h2>
          </div>
          <p className="hidden sm:block font-serif text-sm text-[hsl(40_30%_96%)]/50 italic max-w-xs text-right">
            Eight curated journeys through 150 years of Texas Hill Country heritage
          </p>
        </div>

        {/*
         * Uniform 4-col grid. One featured hero tile spans the full width;
         * every other tile is exactly half-width (md:col-span-2) with a
         * shared min-height and layout via <StandardTile>. Stacks to a
         * single column on mobile.
         */}
        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[minmax(160px,auto)] gap-3 md:gap-4">
          {/* HERO TILE — the single featured position */}
          <PrefetchLink
            to="/museum"
            className="md:col-span-4 relative overflow-hidden border border-brass/20 rounded-sm group bg-[hsl(25_30%_12%)] min-h-[420px] md:min-h-[440px]"
          >
            <BentoTileImage
              src={rangerInterior}
              alt="Y.O. Ranch Headquarters lodge gallery interior, 1990s, displaying archival western saddles, a zebra hide rug, and mounted trophies"
              eager
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(25_25%_8%)] via-[hsl(25_25%_8%)]/40 to-[hsl(25_25%_8%)]/30" />
            <div className="relative z-10 h-full p-6 sm:p-8 md:p-12 flex flex-col justify-between">
              <div>
                <p className="text-brass-light tracking-[0.4em] text-[10px] font-semibold mb-4 sm:mb-6 uppercase">
                  Texas History Museum
                </p>
                <h3 className="font-display text-4xl sm:text-6xl md:text-7xl leading-[0.95] mb-4 text-[hsl(40_35%_94%)]">
                  Step Inside<br />the Archive
                </h3>
                <p className="font-serif text-lg md:text-xl italic text-brass-light/80 max-w-md">
                  Texas Ranger artifacts, frontier firearms, and the living heritage of the Schreiner family.
                </p>
              </div>

              <div className="flex items-center justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[hsl(40_30%_96%)]/40 italic">
                  Strictly by appointment only · registered guests
                </p>
              </div>
            </div>
            <ArrowUpRight className="absolute top-6 right-6 w-5 h-5 text-brass opacity-0 group-hover:opacity-100 transition-opacity" />
          </PrefetchLink>

          <StandardTile
            to="/wildlife-heritage"
            image={exoticWildlifeImg}
            imageAlt="Large herd of Axis deer grazing on the open range at the Y.O. Ranch, Texas Hill Country"
            eyebrow="Natural History"
            title="Exotic Wildlife"
            description="A living conservation program preserving imperiled species on Hill Country rangeland."
          />

          <StandardTile
            to="/exhibits/first-hunting-lease"
            image={firstLeasePage1.url}
            imageAlt="Page 1 of the original 1938 Y.O. Ranch hunting lease — one of the earliest commercial deer leases in America"
            eyebrow="1938: The Deer Lease Is Born"
            eyebrowIcon={FileText}
            title="First Hunting Lease"
            description="The original Y.O. Ranch hunting lease that launched a Texas tradition."
          />

          <StandardTile
            to="/texas-rangers"
            image={rangersImg}
            imageAlt="Portrait photograph of Texas Ranger Captain Manuel 'Lone Wolf' Gonzaullas, Texas Ranger Hall of Fame inductee"
            eyebrow="Law & Legend"
            eyebrowIcon={Shield}
            title="Texas Rangers"
            description="Badges, firearms, and archival accounts of frontier justice."
          />

          <StandardTile
            to="/schreiner-dynasty"
            image={captainSchreinerPortrait}
            imageAlt="Portrait of Captain Charles Schreiner, founder of the Y.O. Ranch and patriarch of the Schreiner dynasty, late 19th century"
            eyebrow="The Legacy"
            eyebrowIcon={Crown}
            title="Schreiner Dynasty"
            description="Five generations of stewardship — from Captain Charles Schreiner's 1880 founding to the family's enduring presence in the Hill Country."
          />

          <StandardTile
            to="/historical-gallery"
            image={rangerInterior}
            imageAlt="Archival photograph reference from the Y.O. Ranch Headquarters historical gallery"
            eyebrow="The Photo Archive"
            eyebrowIcon={Camera}
            title="Archival Photos"
            description="A living visual record of the Hill Country, 1880s to 1980s."
          />

          <StandardTile
            to="/famous-visitors"
            image={visitorImg}
            imageAlt="Princess Grace Kelly and Prince Rainier of Monaco during their 1960 visit to the Y.O. Ranch"
            eyebrow="Hall of Visitors"
            eyebrowIcon={Star}
            title="Famous Visitors"
            description="Monaco royalty, presidents, astronauts, and screen legends."
          />

          <StandardTile
            to="/schreiner-iii"
            image={connallySchreinerIII}
            imageAlt="Charles Schreiner III with Texas Governor John Connally at the Y.O. Ranch"
            eyebrow="Architect of the Modern Y.O."
            title="Charles Schreiner III"
            description="Conservationist, cattleman, Texas Ranger Hall of Fame inductee."
          />

          <StandardTile
            to="/literary-legacy"
            image={vaultLibrary}
            imageAlt="The Y.O. Ranch Headquarters library and writers' gathering room, lined with archival books and western memorabilia"
            eyebrow="The Written West"
            eyebrowIcon={BookOpen}
            title="Literary Legacy"
            description="J. Frank Dobie, James Michener, and the writers who gathered at the headquarters library."
          />

          <StandardTile
            to="/texas-longhorn-heritage"
            image={longhornImg}
            imageAlt="Mounted Texas Longhorn steer trophy from the Y.O. Ranch award ceremony collection"
            eyebrow="Saved From Extinction"
            eyebrowIcon={Compass}
            title="Texas Longhorns"
            description="How the Y.O. Ranch rescued the Texas Longhorn and founded the TLBAA."
          />

          <StandardTile
            to="/native-american-heritage"
            image={firstPeoplesImg}
            imageAlt="Guadalupe River bluffs above the Y.O. Ranch, ancestral lands of the Lipan Apache, Comanche, and Kiowa"
            eyebrow="Before the Brand"
            eyebrowIcon={Feather}
            title="First Peoples of the Y.O."
            description="Ten thousand years of human history on this land — from ancient mound builders to the Lipan Apache and Comanche."
          />
        </div>
      </div>
    </section>
  );
};

export default BentoExplore;